/* eslint-disable max-lines */
import { CHART_EXPORT_TYPE } from '@constants';
import { ECharts } from 'echarts';

import { Nullable } from './common';

export enum RELATIVE_CHARTS {
  VALUE = 'value',
  QUALITY = 'quality',
  PRICE = 'price',
}

export enum ATTRIBUTE_BAR_CHARTS {
  PERFORMANCE = 'performance',
  IMPORTANCE = 'importance',
}

export interface ChartsFiltersForm {
  attributeCompetitor: string;
  relativeQuality: string;
  isSortAsc: Nullable<boolean>;
}

interface ChartPoints {
  id: number;
  name: string;
}

export interface ValueMapChartPoints extends ChartPoints {
  shortName: string;
  mentionsCount: number;
  marketShare: Nullable<number>;
  price: Nullable<number>;
  quality: Nullable<number>;
}

export interface AttributeChartPoints extends ChartPoints {
  ratingDifferences: Nullable<number>;
  importance: Nullable<number>;
  similarPoints?: AttributeChartPoints[];
}

export interface ValueMapData {
  priceImportance: number;
  qualityImportance: number;
  marketDifference: number;
  fairValueLine: Record<string, number>;
  chartPoints: ValueMapChartPoints[];
}

export interface SequenceData {
  seq: number;
  name: string;
}

export interface AttributeImportanceData extends SequenceData {
  importance: Nullable<number>;
}

export interface AttributePerformanceData extends SequenceData {
  importance: Nullable<number>;
  rangeStart: Nullable<number>;
  rangeEnd: Nullable<number>;
  thisBusinessValue: Nullable<number>;
}

export interface RelativeChartDetails {
  evaluationSummary: string;
  scaleInfo: string;
  relativeComparisonChartDtoList: RelativeChartData[];
}

export interface RelativeChartData {
  supplierId: number;
  supplierName: string;
  value: Nullable<number>;
}

export interface PerformanceTableData {
  attributeNames: string[];
  supplierNames: string[];
  performanceValues: number[][];
}

export interface ChartsData {
  valueMap: Nullable<ValueMapData>;
  attributeAnalysisCharts: Nullable<Record<string, AttributeChartPoints[]>>;
  attributeImportanceChart: Nullable<AttributeImportanceData[]>;
  attributeImportanceChartSorted: Nullable<AttributeImportanceData[]>;
  attributePerformanceChart: Nullable<AttributePerformanceData[]>;
  attributePerformanceChartSorted: Nullable<AttributePerformanceData[]>;
  relativeValueChart: Nullable<RelativeChartDetails>;
  relativePriceChart: Nullable<RelativeChartDetails>;
  relativeQualityCharts: Nullable<Record<string, RelativeChartDetails>>;
  relativePerformanceTable: Nullable<PerformanceTableData>;
}

export interface TimeSeriesFilters {
  competitorId: number;
  qualityType: number;
}

export enum CHART_TYPE {
  LINE = 'LINE',
  BAR = 'BAR',
}

export interface DownloadChartProps {
  type: CHART_EXPORT_TYPE;
  width: number;
  height: number;
  title?: string;
}

export interface ChartRef {
  downloadImage: (props: DownloadChartProps) => void;
  getEchartsInstance(): Nullable<ECharts>;
}

export interface AttributeRatingDifference extends SequenceData {
  ratingDifference: Nullable<number>;
}

export interface CompareAggregation {
  valueMap: ValueMapData;
  relativeValueChart: RelativeChartData[];
  relativePriceChart: RelativeChartData[];
  relativeQualityChart: RelativeChartData[];
  attributeAnalysisCharts: Record<string, AttributeChartPoints[]>;
  attributeImportanceChart: AttributeImportanceData[];
  attributePerformanceChart: AttributePerformanceData[];
  averageRatingDifferenceChart: AttributeRatingDifference[];
}

export enum COMPARE_AGGREGATION {
  ATTRIBUTE_ANALYSIS,
  VALUE_COMPARISON,
}

export interface ChartTableData {
  headers: string[];
  data: string[];
}

export interface SimilarPoints {
  id: number;
  name: string;
  price: Nullable<number>;
  quality: Nullable<number>;
  shortName: string;
  mentionsCount: number;
  marketShare: Nullable<number>;
  similarPoints: ValueMapChartPoints[];
}

export interface SimilarPoint {
  id: string;
  name: string;
  price: number;
  quality: number;
  importance: number;
  ratingDifferences: number;
}

export interface CustomChartPoint {
  originalName: string;
  value: [number, number];
  similarPoints?: SimilarPoint[];
}

export interface GroupedAttributePoint extends AttributeChartPoints {
  similarPoints: AttributeChartPoints[];
}

export interface AttributeDiff {
  ratingDifferences: number;
  importance: number;
}

export type NumericAccessor<T> = (point: T) => number | null;

export interface GroupingConfig<T> {
  accessors: Record<string, NumericAccessor<T>>;
  thresholds: Record<string, number>;
}

export enum TOOLTIP_POSITION {
  START = 'start',
  END = 'end',
}

export interface ChartThemeColors {
  label: string;
  line25: string;
  line50: string;
  line100: string;
  item: string;
  transition: string;
  baseLine: string;
  background: string;
  timeSeriesLines: string[];
}
